import React, { useState, useEffect, useRef } from 'react';

const LandingPage = ({ onEnter }) => {
    const [visibleSections, setVisibleSections] = useState({});
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const sectionRefs = useRef({});

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setVisibleSections(prev => ({ ...prev, [entry.target.id]: true }));
                    }
                });
            },
            { threshold: 0.1 }
        );
        Object.values(sectionRefs.current).forEach(ref => {
            if (ref) observer.observe(ref);
        });
        return () => observer.disconnect();
    }, []);

    const setRef = (id) => (el) => {
        sectionRefs.current[id] = el;
    };

    const features = [
        { icon: '⚡', title: 'Lançamento em segundos', desc: 'Adicione entradas e saídas em poucos toques. Sem formulários complexos, sem perda de tempo.' },
        { icon: '📊', title: 'Visão clara do mês', desc: 'Gráficos e resumos automáticos para você entender para onde vai cada centavo.' },
        { icon: '🔔', title: 'Alertas inteligentes', desc: 'Receba notificações antes de contas vencerem. Nunca mais pague juros por esquecimento.' },
        { icon: '🔄', title: 'Sincronização em tempo real', desc: 'Seus dados salvos na nuvem com Firebase. Acesse de qualquer dispositivo, sempre atualizado.' },
        { icon: '📱', title: 'Feito para mobile', desc: 'Interface otimizada para celular. Controle seu financeiro onde estiver, quando quiser.' },
        { icon: '🔒', title: 'Seguro e privado', desc: 'Login com Google. Seus dados são seus — protegidos e nunca compartilhados.' },
    ];

    const plans = [
        {
            emoji: '🚀',
            name: 'Começar',
            price: 'R$ 9',
            period: '/mês',
            badge: null,
            desc: 'Para sair do caderno e organizar o básico.',
            featured: false,
            cta: 'Testar 7 dias grátis',
            features: [
                { text: '100 lançamentos por mês', ok: true },
                { text: '1 conta financeira', ok: true },
                { text: 'Dashboard mensal simples', ok: true },
                { text: 'Alertas automáticos', ok: false },
                { text: 'Metas financeiras', ok: false },
                { text: 'Exportação PDF/Excel', ok: false },
                { text: 'Relatórios avançados', ok: false },
            ],
        },
        {
            emoji: '📈',
            name: 'Crescer',
            price: 'R$ 19',
            period: '/mês',
            badge: 'Mais popular',
            desc: 'Para acompanhar melhor entradas e despesas.',
            featured: true,
            cta: 'Testar 7 dias grátis',
            features: [
                { text: 'Lançamentos ilimitados', ok: true },
                { text: 'Até 3 contas financeiras', ok: true },
                { text: 'Metas financeiras', ok: true },
                { text: 'Alertas de contas a vencer', ok: true },
                { text: 'Relatório mensal detalhado', ok: true },
                { text: 'Categorias personalizadas', ok: true },
                { text: 'Comparação mês atual vs anterior', ok: true },
                { text: 'Exportação PDF/Excel', ok: false },
            ],
        },
        {
            emoji: '🏆',
            name: 'Dominar',
            price: 'R$ 39',
            period: '/mês',
            badge: null,
            desc: 'Para controle total e metas avançadas.',
            featured: false,
            cta: 'Testar 7 dias grátis',
            features: [
                { text: 'Tudo do plano Crescer', ok: true },
                { text: 'Contas ilimitadas', ok: true },
                { text: 'Relatórios avançados com gráficos', ok: true },
                { text: 'Comparação de vários meses', ok: true },
                { text: 'Exportação PDF e Excel', ok: true },
                { text: 'Backup automático', ok: true },
                { text: 'Suporte prioritário', ok: true },
            ],
        },
    ];

    const testimonials = [
        { quote: 'Finalmente um app de finanças que não parece uma planilha de RH. Simples e direto.', name: 'Giulia A.', role: 'Freelancer' },
        { quote: 'Uso todo mês pra fechar as contas. Em 5 minutos já sei se tô no azul ou no vermelho.', name: 'Gabrielli R.', role: 'MEI' },
        { quote: 'O alerta de vencimento salvou minha vida. Nunca mais paguei multa por esquecimento.', name: 'David F.', role: 'Autônomo' },
    ];

    return (
        <div style={{ background: '#05080b', fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif', color: 'white', overflowX: 'hidden' }}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Syne:wght@700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }

        .fade-up { opacity: 0; transform: translateY(32px); transition: opacity 0.6s ease, transform 0.6s ease; }
        .fade-up.visible { opacity: 1; transform: translateY(0); }
        .fade-up-delay-1 { transition-delay: 0.1s; }
        .fade-up-delay-2 { transition-delay: 0.2s; }
        .fade-up-delay-3 { transition-delay: 0.3s; }
        .fade-up-delay-4 { transition-delay: 0.4s; }
        .fade-up-delay-5 { transition-delay: 0.5s; }

        .nav-blur {
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          background: rgba(5,8,11,0.85);
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }
        .feature-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 20px;
          padding: 24px;
          transition: all 0.3s ease;
        }
        .feature-card:hover {
          background: rgba(255,255,255,0.06);
          border-color: rgba(34,197,94,0.3);
          transform: translateY(-4px);
        }
        .plan-card {
          background: rgba(255,255,255,0.03);
          border-radius: 24px;
          padding: 32px;
          position: relative;
          transition: all 0.3s ease;
        }
        .plan-card.featured { background: rgba(34,197,94,0.05); }
        .check-item {
          display: flex;
          align-items: center;
          font-size: 14px;
          color: #d1d5db;
          padding: 6px 0;
        }
        .check-item::before {
          content: '✓';
          color: #22c55e;
          font-weight: 700;
          margin-right: 10px;
          flex-shrink: 0;
        }
        .mockup-screen {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 16px;
          overflow: hidden;
        }
        .stat-card {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 14px;
          padding: 14px 16px;
        }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        .float-anim { animation: float 4s ease-in-out infinite; }
        @keyframes ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        .ticker-track { display: flex; width: max-content; animation: ticker 20s linear infinite; }
        .gradient-text {
          background: linear-gradient(135deg, #22c55e, #10b981, #6ee7b7);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        @keyframes pulse-dot { 0%,100%{opacity:1} 50%{opacity:0.4} }
        .lp-btn-primary {
          background: linear-gradient(135deg, #22c55e, #10b981);
          border: none; color: white; border-radius: 14px;
          font-size: 16px; font-weight: 700; cursor: pointer;
          box-shadow: 0 8px 32px rgba(34,197,94,0.3);
          transition: all 0.2s; text-align: center;
          padding: 15px 28px; width: 100%;
        }
        .lp-btn-secondary {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          color: white; border-radius: 14px;
          font-size: 16px; font-weight: 500; cursor: pointer;
          transition: all 0.2s; text-align: center;
          padding: 15px 28px; width: 100%;
        }
        .lp-btn-primary:hover { opacity: 0.9; transform: translateY(-2px); }
        .lp-btn-secondary:hover { background: rgba(255,255,255,0.1); }
      `}</style>

            {/* NAVBAR */}
            <nav className="nav-blur" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100 }}>
                <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 20px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <img src={process.env.PUBLIC_URL + '/logo.png'} alt="FinTrack" style={{ width: 30, height: 30, borderRadius: 8 }} />
                        <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 17 }}>FinTrack</span>
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {!isMobile && (
                            <button onClick={onEnter}
                                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'white', padding: '7px 18px', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
                            >Entrar</button>
                        )}
                        <button onClick={onEnter}
                            style={{ background: '#22c55e', border: 'none', color: 'white', padding: '8px 18px', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                        >Testar 7 dias grátis</button>
                    </div>
                </div>
            </nav>

            {/* HERO */}
            <section style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', position: 'relative', padding: isMobile ? '100px 20px 60px' : '120px 24px 80px', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', width: 500, height: 500, background: 'rgba(34,197,94,0.1)', borderRadius: '50%', filter: 'blur(80px)', top: -100, left: -150, pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', width: 350, height: 350, background: 'rgba(16,185,129,0.07)', borderRadius: '50%', filter: 'blur(80px)', bottom: 0, right: -100, pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

                <div style={{ maxWidth: 1100, margin: '0 auto', width: '100%', position: 'relative', zIndex: 1 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 48 : 80, alignItems: 'center' }}>

                        <div>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 100, padding: '5px 14px', marginBottom: 24, fontSize: 12, color: '#4ade80', fontWeight: 500 }}>
                                <span style={{ width: 6, height: 6, background: '#22c55e', borderRadius: '50%', animation: 'pulse-dot 2s infinite' }} />
                                7 dias grátis · Sem cartão de crédito
                            </div>

                            <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: isMobile ? 36 : 56, fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.5px', marginBottom: 20 }}>
                                Tenha clareza do<br />seu dinheiro<br />
                                <span className="gradient-text">Sem planilhas.</span>
                            </h1>

                            <p style={{ fontSize: isMobile ? 15 : 18, color: '#9ca3af', lineHeight: 1.7, marginBottom: 36, maxWidth: 460 }}>
                                Organize entradas, saídas e metas em um só lugar. Fuja do caos financeiro e tome decisões com confiança.
                            </p>

                            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12 }}>
                                <button className="lp-btn-primary" onClick={onEnter}>Testar 7 dias grátis →</button>
                                <button className="lp-btn-secondary" onClick={onEnter}>Ver demonstração</button>
                            </div>

                            <div style={{ display: 'flex', gap: isMobile ? 24 : 32, marginTop: 40, paddingTop: 28, borderTop: '1px solid rgba(255,255,255,0.06)', flexWrap: 'wrap' }}>
                                {[['7 dias', 'Teste grátis'], ['Firebase', 'Dados seguros'], ['iOS + Android', 'Em breve']].map(([val, label]) => (
                                    <div key={label}>
                                        <div style={{ fontSize: isMobile ? 15 : 18, fontWeight: 700, color: '#22c55e', fontFamily: 'Syne, sans-serif' }}>{val}</div>
                                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{label}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Mockup — desktop only */}
                        {!isMobile && (
                            <div className="float-anim" style={{ position: 'relative' }}>
                                <div style={{ position: 'absolute', inset: -20, background: 'radial-gradient(circle, rgba(34,197,94,0.12), transparent 70%)', zIndex: 0 }} />
                                <div className="mockup-screen" style={{ position: 'relative', zIndex: 1 }}>
                                    <div style={{ background: 'rgba(255,255,255,0.03)', padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <img src={process.env.PUBLIC_URL + '/logo.png'} alt="FinTrack" style={{ width: 22, height: 22, borderRadius: 6 }} />
                                            <span style={{ fontSize: 13, fontWeight: 700 }}>FinTrack</span>
                                        </div>
                                        <span style={{ fontSize: 11, color: '#4ade80', background: 'rgba(34,197,94,0.1)', padding: '2px 10px', borderRadius: 20 }}>● Online</span>
                                    </div>
                                    <div style={{ padding: 18 }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                                            <div className="stat-card">
                                                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>Entradas</div>
                                                <div style={{ fontSize: 20, fontWeight: 700, color: '#22c55e' }}>R$ 5.200</div>
                                            </div>
                                            <div className="stat-card">
                                                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>A Pagar</div>
                                                <div style={{ fontSize: 20, fontWeight: 700, color: '#f97316' }}>R$ 1.840</div>
                                            </div>
                                        </div>
                                        <div style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(16,185,129,0.05))', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 12, padding: '14px 16px', marginBottom: 14 }}>
                                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>Saldo Real</div>
                                            <div style={{ fontSize: 24, fontWeight: 800, fontFamily: 'Syne, sans-serif' }}>R$ 3.360,00</div>
                                            <div style={{ fontSize: 11, color: '#4ade80', marginTop: 3 }}>▲ 12% em relação ao mês anterior</div>
                                        </div>
                                        <div style={{ marginBottom: 14 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6b7280', marginBottom: 6 }}>
                                                <span>Meta mensal</span><span style={{ color: '#4ade80' }}>67%</span>
                                            </div>
                                            <div style={{ height: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 3 }}>
                                                <div style={{ height: '100%', width: '67%', background: 'linear-gradient(90deg, #22c55e, #10b981)', borderRadius: 3 }} />
                                            </div>
                                        </div>
                                        {[
                                            { icon: '💰', name: 'Salário', cat: 'Entrada', val: '+R$ 4.500', color: '#22c55e' },
                                            { icon: '🏠', name: 'Aluguel', cat: 'Moradia', val: '-R$ 1.200', color: '#ef4444' },
                                            { icon: '🛒', name: 'Supermercado', cat: 'Alimentação', val: '-R$ 380', color: '#ef4444' },
                                        ].map((t, i, arr) => (
                                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <div style={{ fontSize: 16 }}>{t.icon}</div>
                                                    <div>
                                                        <div style={{ fontSize: 12, fontWeight: 500 }}>{t.name}</div>
                                                        <div style={{ fontSize: 10, color: '#6b7280' }}>{t.cat}</div>
                                                    </div>
                                                </div>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: t.color }}>{t.val}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* TICKER */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '12px 0', overflow: 'hidden', background: 'rgba(34,197,94,0.03)' }}>
                <div className="ticker-track">
                    {[...Array(2)].map((_, ri) => (
                        <div key={ri} style={{ display: 'flex', gap: 40, paddingRight: 40 }}>
                            {['Controle total', 'Sem planilhas', 'Login com Google', 'Dados seguros', 'Grátis para começar', 'Relatórios automáticos', 'Alertas inteligentes', 'iOS & Android em breve'].map((t, i) => (
                                <span key={i} style={{ fontSize: 12, color: '#4b5563', fontWeight: 500, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ color: '#22c55e' }}>✦</span> {t}
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {/* FEATURES */}
            <section id="features" ref={setRef('features')} style={{ padding: isMobile ? '64px 20px' : '100px 24px', maxWidth: 1100, margin: '0 auto' }}>
                <div className={`fade-up ${visibleSections.features ? 'visible' : ''}`} style={{ textAlign: 'center', marginBottom: isMobile ? 36 : 64 }}>
                    <div style={{ fontSize: 12, color: '#4ade80', fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 14 }}>Funcionalidades</div>
                    <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: isMobile ? 26 : 42, fontWeight: 800, letterSpacing: '-1px', marginBottom: 14, lineHeight: 1.2 }}>
                        Tudo que você precisa,<br />nada que você não precisa
                    </h2>
                    <p style={{ fontSize: isMobile ? 14 : 17, color: '#6b7280', maxWidth: 480, margin: '0 auto', lineHeight: 1.6 }}>
                        Simples por fora, poderoso por dentro. Desenhado para quem quer resultado, não complexidade.
                    </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: 14 }}>
                    {features.map((f, i) => (
                        <div key={i} className={`feature-card fade-up fade-up-delay-${Math.min(i + 1, 5)} ${visibleSections.features ? 'visible' : ''}`}>
                            <div style={{ fontSize: isMobile ? 24 : 28, marginBottom: 12 }}>{f.icon}</div>
                            <div style={{ fontSize: isMobile ? 13 : 15, fontWeight: 700, marginBottom: 6 }}>{f.title}</div>
                            <div style={{ fontSize: isMobile ? 12 : 14, color: '#6b7280', lineHeight: 1.6 }}>{f.desc}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section style={{ padding: isMobile ? '48px 20px' : '60px 24px', background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? 16 : 0 }}>
                        {testimonials.map((t, i) => (
                            <div key={i} style={{
                                padding: isMobile ? '20px' : '32px',
                                borderRight: !isMobile && i < 2 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                                background: isMobile ? 'rgba(255,255,255,0.03)' : 'transparent',
                                borderRadius: isMobile ? 16 : 0,
                                border: isMobile ? '1px solid rgba(255,255,255,0.06)' : 'none',
                            }}>
                                <div style={{ fontSize: 26, color: '#22c55e', marginBottom: 10, lineHeight: 1 }}>"</div>
                                <p style={{ fontSize: isMobile ? 13 : 14, color: '#d1d5db', lineHeight: 1.7, marginBottom: 16 }}>{t.quote}</p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #22c55e, #10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{t.name[0]}</div>
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</div>
                                        <div style={{ fontSize: 11, color: '#6b7280' }}>{t.role}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section id="pricing" ref={setRef('pricing')} style={{ padding: isMobile ? '64px 20px' : '100px 24px', maxWidth: 1100, margin: '0 auto' }}>
                <div className={`fade-up ${visibleSections.pricing ? 'visible' : ''}`} style={{ textAlign: 'center', marginBottom: isMobile ? 36 : 64 }}>
                    <div style={{ fontSize: 12, color: '#4ade80', fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 14 }}>Planos</div>
                    <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: isMobile ? 26 : 42, fontWeight: 800, letterSpacing: '-1px', marginBottom: 12 }}>
                        Preço justo, valor real
                    </h2>
                    <p style={{ fontSize: isMobile ? 14 : 17, color: '#6b7280' }}>7 dias grátis. Cancele quando quiser.</p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 20, maxWidth: 1000, margin: '0 auto' }}>
                    {plans.map((plan, i) => (
                        <div key={i} className={`plan-card fade-up fade-up-delay-${i + 1} ${visibleSections.pricing ? 'visible' : ''} ${plan.featured ? 'featured' : ''}`}
                            style={{ border: `2px solid ${plan.featured ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.08)'}` }}
                        >
                            {plan.badge && (
                                <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #22c55e, #10b981)', color: 'white', fontSize: 11, fontWeight: 700, padding: '4px 14px', borderRadius: 100, whiteSpace: 'nowrap' }}>
                                    {plan.badge}
                                </div>
                            )}
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: 24, marginBottom: 6 }}>{plan.emoji}</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: 'white', marginBottom: 4 }}>{plan.name}</div>
                                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>{plan.desc}</div>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                                    <span style={{ fontFamily: 'Syne, sans-serif', fontSize: isMobile ? 32 : 36, fontWeight: 800 }}>{plan.price}</span>
                                    <span style={{ fontSize: 13, color: '#6b7280' }}>{plan.period}</span>
                                </div>
                            </div>
                            <div style={{ marginBottom: 24, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16 }}>
                                {plan.features.map((f, j) => (
                                    <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 13 }}>
                                        <span style={{ color: f.ok ? '#22c55e' : '#374151', fontWeight: 700, flexShrink: 0 }}>{f.ok ? '✓' : '✗'}</span>
                                        <span style={{ color: f.ok ? '#d1d5db' : '#4b5563' }}>{f.text}</span>
                                    </div>
                                ))}
                            </div>
                            <button onClick={onEnter}
                                style={{ width: '100%', padding: '13px', borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer', border: 'none', background: plan.featured ? 'linear-gradient(135deg, #22c55e, #10b981)' : 'rgba(255,255,255,0.08)', color: 'white', boxShadow: plan.featured ? '0 8px 24px rgba(34,197,94,0.25)' : 'none' }}
                            >{plan.cta}</button>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA FINAL */}
            <section id="cta" ref={setRef('cta')} style={{ padding: isMobile ? '20px 20px 60px' : '60px 24px 80px', maxWidth: 1100, margin: '0 auto' }}>
                <div className={`fade-up ${visibleSections.cta ? 'visible' : ''}`}
                    style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.12), rgba(16,185,129,0.05))', border: '1px solid rgba(34,197,94,0.2)', borderRadius: isMobile ? 20 : 32, padding: isMobile ? '48px 24px' : '72px 48px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}
                >
                    <div style={{ position: 'absolute', top: -60, right: -60, width: 250, height: 250, background: 'rgba(34,197,94,0.08)', borderRadius: '50%', filter: 'blur(60px)' }} />
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <div style={{ fontSize: 12, color: '#4ade80', fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>Comece agora</div>
                        <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: isMobile ? 24 : 44, fontWeight: 800, letterSpacing: '-1px', marginBottom: 16, lineHeight: 1.2 }}>
                            Seu financeiro organizado<br />em menos de 5 minutos
                        </h2>
                        <p style={{ fontSize: isMobile ? 14 : 16, color: '#9ca3af', marginBottom: 32, maxWidth: 440, margin: '0 auto 32px', lineHeight: 1.6 }}>
                            Crie sua conta grátis, adicione suas primeiras transações e já veja a diferença no seu controle financeiro.
                        </p>
                        <button onClick={onEnter}
                            style={{ background: 'linear-gradient(135deg, #22c55e, #10b981)', border: 'none', color: 'white', padding: isMobile ? '14px 32px' : '18px 48px', borderRadius: 14, fontSize: isMobile ? 15 : 18, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 32px rgba(34,197,94,0.35)' }}
                        >Testar 7 dias grátis →</button>
                        <p style={{ fontSize: 12, color: '#4b5563', marginTop: 14 }}>Sem cartão de crédito. Sem compromisso.</p>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer style={{ borderTop: '1px solid rgba(255,255,255,0.05)', padding: isMobile ? '28px 20px' : '40px 24px', maxWidth: 1100, margin: '0 auto' }}>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <img src={process.env.PUBLIC_URL + '/logo.png'} alt="FinTrack" style={{ width: 26, height: 26, borderRadius: 7 }} />
                        <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 15 }}>FinTrack</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#4b5563' }}>© 2025 FinTrack · fintrackapp.com.br</div>
                    <div style={{ display: 'flex', gap: 20 }}>
                        {['Privacidade', 'Termos', 'Contato'].map(l => (
                            <span key={l} style={{ fontSize: 12, color: '#4b5563', cursor: 'pointer' }}>{l}</span>
                        ))}
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
